import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Trash2, Loader2 } from 'lucide-react';

interface CleanupConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  confirmName: string;
  onConfirmNameChange: (value: string) => void;
  expectedName: string | undefined;
  isDeleting: boolean;
  onConfirm: () => void;
  translationPrefix: string;
}

export function CleanupConfirmDialog({
  open,
  onOpenChange,
  confirmName,
  onConfirmNameChange,
  expectedName,
  isDeleting,
  onConfirm,
  translationPrefix,
}: CleanupConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) onConfirmNameChange('');
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${translationPrefix}.confirmTitle`)}</DialogTitle>
          <DialogDescription>{t(`${translationPrefix}.confirmDescription`)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="confirmName">{t(`${translationPrefix}.confirmLabel`)}</Label>
            <Input
              id="confirmName"
              value={confirmName}
              onChange={(e) => onConfirmNameChange(e.target.value)}
              placeholder=""
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); onConfirmNameChange(''); }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting || confirmName !== expectedName}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t(`${translationPrefix}.deleting`)}
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                {t(`${translationPrefix}.confirmButton`)}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
